import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useParams } from 'react-router';
import { useQueries, useQuery } from 'react-query';
import * as RestApi from 'core/network/RestApi';
import { ITemplate } from 'entities/template/template';
import { getTemplates } from 'sections/design/template-management/sections/template-list/utils/queries';
import { TemplateApiEndpoints } from 'sections/design/template-management/constants/api-endpoints';

/**
 * Fetches SMS templates for the current site and their body content (for preview).
 * Content queries run only when contentQueriesEnabled is true (e.g. when modal is open).
 */
export function useSmsTemplatesWithContent(selectedLanguage: string, contentQueriesEnabled: boolean) {
  const { t } = useTranslation('designs');
  const { siteId } = useParams<{ siteId: string }>();

  const smsTemplatesQuery = useQuery(['templates', siteId], () => getTemplates({ siteId: siteId!, t }), {
    enabled: Boolean(siteId),
    select: (data: ITemplate[]) => data.filter(template => template.channel === 'sms'),
  });

  const smsTemplates = useMemo(() => {
    const channelSms = smsTemplatesQuery.data ?? [];
    return channelSms.filter(template => template.supportedLanguages?.some(s => s.language === selectedLanguage));
  }, [smsTemplatesQuery.data, selectedLanguage]);

  const templateContentQueries = useQueries(
    smsTemplates.map(template => {
      const language = template.supportedLanguages?.some(s => s.language === selectedLanguage)
        ? selectedLanguage
        : template.supportedLanguages?.[0]?.language ?? selectedLanguage;
      return {
        queryKey: ['smsTemplateContent', siteId, template.id, language],
        queryFn: async (): Promise<string> => {
          const result = await RestApi.get<ITemplate>(
            generatePath(TemplateApiEndpoints.GET_TEMPLATE, {
              siteId: siteId!,
              templateId: template.id,
              language,
            }),
          );
          return result.data?.template ?? '';
        },
        enabled: Boolean(contentQueriesEnabled && siteId && template.id),
      };
    }),
  );

  const templateContentById = useMemo(() => {
    const map: Record<string, string> = {};
    smsTemplates.forEach((tmpl, i) => {
      const content = templateContentQueries[i]?.data as string | undefined;
      if (content !== undefined) map[tmpl.id] = content;
      else if (tmpl.template) map[tmpl.id] = tmpl.template;
    });
    return map;
  }, [smsTemplates, templateContentQueries]);

  const getTemplatePreviewContent = useCallback(
    (template: ITemplate) => templateContentById[template.id] ?? template.template ?? '',
    [templateContentById],
  );

  const templatesQuery = useMemo(
    () => ({
      data: smsTemplates,
      isLoading: smsTemplatesQuery.isLoading,
      isError: smsTemplatesQuery.isError,
    }),
    [smsTemplates, smsTemplatesQuery.isLoading, smsTemplatesQuery.isError],
  );

  return {
    smsTemplates,
    templateContentById,
    getTemplatePreviewContent,
    templatesQuery,
    isLoading: smsTemplatesQuery.isLoading,
  };
}
